import type { EffectDefinition } from './types';
import { SunRaysEffect } from './effects/environmental/sunRays';
import { RainEffect } from './effects/environmental/rain';
import { LensDropsEffect } from './effects/environmental/lensDrops';
import { SnowEffect } from './effects/environmental/snow';
import { FogEffect } from './effects/environmental/fog';
import { FireEmbersEffect } from './effects/environmental/fireEmbers';
import { DustParticlesEffect } from './effects/environmental/dustParticles';
import { LightLeaksEffect } from './effects/environmental/lightLeaks';
import { LightningEffect } from './effects/environmental/lightning';
import { CinematicCloudsEffect } from './effects/environmental/cinematicClouds';

/** Environmental & cinematic effect definitions (effects 6–15). */
export const ENVIRONMENTAL_EFFECT_DEFINITIONS: EffectDefinition[] = [
  {
    type: 'sun_rays',
    label: 'Sunshine / God Rays',
    category: 'environmental',
    defaultParameters: { intensity: 1.05, angle: 38, spread: 0.95, colorR: 1, colorG: 0.9, colorB: 0.62 },
    parameterSchema: [
      { key: 'intensity', label: 'Intensity', min: 0, max: 2, step: 0.05 },
      { key: 'angle', label: 'Angle (°)', min: 0, max: 360, step: 1 },
      { key: 'spread', label: 'Spread', min: 0.1, max: 1.5, step: 0.05 },
      { key: 'colorR', label: 'Color R', min: 0, max: 1, step: 0.05 },
      { key: 'colorG', label: 'Color G', min: 0, max: 1, step: 0.05 },
      { key: 'colorB', label: 'Color B', min: 0, max: 1, step: 0.05 },
    ],
    create: (id, clip) => new SunRaysEffect(id, clip),
  },
  {
    type: 'rain',
    label: 'Rain',
    category: 'weather',
    defaultParameters: { density: 0.75, speed: 1.1, wind: 0.25, dropSize: 0.65 },
    parameterSchema: [
      { key: 'density', label: 'Density', min: 0, max: 1, step: 0.05 },
      { key: 'speed', label: 'Speed', min: 0, max: 3, step: 1 },
      { key: 'wind', label: 'Wind', min: -1, max: 1, step: 0.05 },
      { key: 'dropSize', label: 'Drop Size', min: 0.1, max: 2, step: 0.1 },
    ],
    create: (id, clip) => new RainEffect(id, clip),
  },
  {
    type: 'lens_drops',
    label: 'Water Droplets on Lens',
    category: 'lens',
    defaultParameters: { count: 20, size: 0.5, refraction: 0.8 },
    parameterSchema: [
      { key: 'count', label: 'Count', min: 1, max: 64, step: 1 },
      { key: 'size', label: 'Size', min: 0.1, max: 2, step: 0.1 },
      { key: 'refraction', label: 'Refraction', min: 0, max: 2, step: 0.05 },
    ],
    create: (id, clip) => new LensDropsEffect(id, clip),
  },
  {
    type: 'snow',
    label: 'Snow',
    category: 'weather',
    defaultParameters: { density: 0.65, wind: 0.25, flakeSize: 0.75 },
    parameterSchema: [
      { key: 'density', label: 'Density', min: 0, max: 1, step: 0.05 },
      { key: 'wind', label: 'Wind', min: -1, max: 1, step: 0.05 },
      { key: 'flakeSize', label: 'Flake Size', min: 0.1, max: 2, step: 0.1 },
    ],
    create: (id, clip) => new SnowEffect(id, clip),
  },
  {
    type: 'fog',
    label: 'Fog',
    category: 'environmental',
    defaultParameters: { density: 0.4, speed: 0.2, height: 0.6 },
    parameterSchema: [
      { key: 'density', label: 'Density', min: 0, max: 1, step: 0.05 },
      { key: 'speed', label: 'Speed', min: 0, max: 1, step: 1 },
      { key: 'height', label: 'Height', min: 0.1, max: 2, step: 0.05 },
    ],
    create: (id, clip) => new FogEffect(id, clip),
  },
  {
    type: 'fire_embers',
    label: 'Fire Embers',
    category: 'particles',
    defaultParameters: { count: 650, brightness: 1.05, speed: 1.1 },
    parameterSchema: [
      { key: 'count', label: 'Count', min: 50, max: 600, step: 10 },
      { key: 'brightness', label: 'Brightness', min: 0, max: 2, step: 0.05 },
      { key: 'speed', label: 'Speed', min: 0, max: 3, step: 1 },
    ],
    create: (id, clip) => new FireEmbersEffect(id, clip),
  },
  {
    type: 'dust_particles',
    label: 'Dust Particles',
    category: 'particles',
    defaultParameters: { density: 0.4, speed: 0.1 },
    parameterSchema: [
      { key: 'density', label: 'Density', min: 0, max: 1, step: 0.05 },
      { key: 'speed', label: 'Drift Speed', min: 0, max: 1, step: 1 },
    ],
    create: (id, clip) => new DustParticlesEffect(id, clip),
  },
  {
    type: 'light_leaks',
    label: 'Light Leaks',
    category: 'cinematic',
    defaultParameters: { intensity: 0.7, colorMode: 0 },
    parameterSchema: [
      { key: 'intensity', label: 'Intensity', min: 0, max: 2, step: 0.05 },
      { key: 'colorMode', label: 'Color Mode (0=warm,1=cool,2=rainbow)', min: 0, max: 2, step: 1 },
    ],
    create: (id, clip) => new LightLeaksEffect(id, clip),
  },
  {
    type: 'lightning',
    label: 'Lightning',
    category: 'weather',
    defaultParameters: { frequency: 0.3, brightness: 1 },
    parameterSchema: [
      { key: 'frequency', label: 'Frequency', min: 0, max: 1, step: 0.05 },
      { key: 'brightness', label: 'Brightness', min: 0, max: 2, step: 0.05 },
    ],
    create: (id, clip) => new LightningEffect(id, clip),
  },
  {
    type: 'cinematic_clouds',
    label: 'Cinematic Clouds',
    category: 'environmental',
    defaultParameters: { coverage: 0.7, speed: 0.2 },
    parameterSchema: [
      { key: 'coverage', label: 'Coverage', min: 0, max: 1, step: 0.05 },
      { key: 'speed', label: 'Drift Speed', min: 0, max: 1, step: 1 },
    ],
    create: (id, clip) => new CinematicCloudsEffect(id, clip),
  },
];

/** Environmental subset order (canonical full stack lives in registry.ts). */
export const ENVIRONMENTAL_EFFECT_ORDER = [
  'fog',
  'cinematic_clouds',
  'sun_rays',
  'rain',
  'snow',
  'lens_drops',
  'heat_distortion',
  'dust_particles',
  'fire_embers',
  'light_leaks',
  'lightning',
] as const;
